import { TemplatesService, canConsumeTemplate } from '@beeboard/common';
import { defineStore } from 'pinia';
import { useActivityTemplateRulesStore } from '../activitytemplateRulesStore';
import { useActivityTemplateStore } from '../activityTemplateStore';
import { useTemplateRulesStore } from '../templateRulesStore';
import { useUserStore } from '../userStore';
import type {
  ActivityTemplateSummary,
  CompanyDocument,
  CompanyLink,
  CompletionCriterionTemplate,
  CompletionCriterionTemplateCreate,
  CompletionCriterionTemplateUpdate,
  CreateDocumentTemplateRequest,
  CreateLinkTemplateRequest,
  CreateProjectFunctionRequest,
  CreateProjectMapTemplateRequest,
  DocumentTemplate,
  LinkTemplate,
  MilestoneTemplateSummary,
  PhaseTemplate,
  PhaseTemplateUpdate,
  ProjectMapTemplate,
  ProjectMapTemplateLink,
  TagTemplate,
  UpdateDocumentTemplateRequest,
  UpdateLinkTemplateRequest,
  UpdateProjectFunctionRequest,
  UpdateProjectMapTemplateRequest,
  UserSummary,
  WorkstreamTemplate,
  WorkstreamTemplateCreate,
  WorkstreamTemplateUpdate,
  TemplateDetails,
  UpdateProjectMapTemplateDetailsRequest,
  MoveCompletionCriterionTemplateRequest,
  MergeFunctionRequest,
  TemplateStatus,
} from '@beeboard/common';

export type Milestone = MilestoneTemplateSummary & {
  metadata: {
    phaseId: string;
    isFinal: boolean;
  };
};

/**
 * The request that picks a company link into a template (WI #5775). The server
 * takes title, link and type from the company link itself; they are sent all
 * the same so that the request is complete on its own.
 */
export function toCompanyLinkPickRequest(
  companyLink: CompanyLink,
  activityId?: string,
): CreateLinkTemplateRequest {
  return {
    title: companyLink.title,
    link: companyLink.link ?? undefined,
    mime_type: companyLink.mime_type,
    parent: companyLink.id,
    activity_id: activityId,
  };
}

/**
 * The request that picks a company document into a template (WI #5775). The
 * server takes title, type and relative path from the company document itself.
 */
export function toCompanyDocumentPickRequest(
  companyDocument: CompanyDocument,
  activityId?: string,
): CreateDocumentTemplateRequest {
  return {
    title: companyDocument.title,
    mime_type: companyDocument.mime_type ?? undefined,
    relative_path: companyDocument.relative_path ?? undefined,
    parent: companyDocument.id,
    activity_id: activityId,
  };
}

export type TemplateBasics = Pick<
  TemplateDetails,
  'id' | 'title' | 'scopes' | 'owner' | 'status' | 'custom_properties'
>;

export type TemplateStoreState = {
  templates: ProjectMapTemplate[];
  templateLinks: ProjectMapTemplateLink[];
  templatesDetails: TemplateDetails[];
  editorTemplates: Record<string, UserSummary[]>;
  isListView: boolean;
};

export const useTemplateStore = defineStore('templateStore', {
  state: (): TemplateStoreState => ({
    templates: [],
    templateLinks: [],
    templatesDetails: [],
    editorTemplates: {},
    isListView: false,
  }),

  getters: {
    template(state) {
      return (id: string) => {
        const template = state.templates.find((template) => template.id === id);
        if (!template) {
          throw new Error('Missing template');
        }

        return template;
      };
    },

    hasTemplate(state) {
      return (templateId: string) => {
        return state.templates.some((template) => template.id === templateId);
      };
    },

    templateLink(state) {
      return (id: string) =>
        state.templateLinks.find((template) => template.id === id);
    },

    // Templates that may actually become project content. Uses the same rule the
    // server enforces, so the pickers and the API cannot disagree (#5667).
    consumableTemplateLinks(state) {
      return state.templateLinks.filter((template) =>
        canConsumeTemplate(template.status),
      );
    },

    templateSummary(state) {
      return (id: string) => {
        const summary = state.templateLinks.find(
          (template) => template.id === id,
        );
        if (!summary) {
          throw new Error('Missing template summary');
        }

        return summary;
      };
    },
    hasTemplateSummary(state) {
      return (templateId: string) => {
        return state.templateLinks.some(
          (template) => template.id === templateId,
        );
      };
    },

    templateDetails(state) {
      return (templateId: string): TemplateDetails => {
        const template = state.templatesDetails.find(
          (template) => template.id === templateId,
        );
        if (!template) {
          throw new Error('Missing template details');
        }

        return template;
      };
    },

    // `fromDetails`: Company → Templates reads its own list first, which it has just
    // loaded; an opened map or the template list may hold an older copy.
    templateBasics(state) {
      return (
        templateId: string,
        fromDetails = false,
      ): TemplateBasics | undefined => {
        const details = state.templatesDetails.find(
          (template) => template.id === templateId,
        );
        if (fromDetails && details) {
          return details;
        }

        return (
          state.templates.find((template) => template.id === templateId) ??
          state.templateLinks.find((template) => template.id === templateId) ??
          details
        );
      };
    },

    templateTitle() {
      return (projectId: string) => {
        if (this.hasTemplateSummary(projectId)) {
          return this.templateSummary(projectId).title;
        }
        if (this.hasTemplate(projectId)) {
          return this.template(projectId).title;
        }

        return '';
      };
    },

    // Same fallback as templateTitle, and for the same reason: the map route
    // loads only the full template, so on a direct load or a refresh there is no
    // summary to read the status off (#5667).
    templateStatus() {
      return (templateId: string): TemplateStatus | undefined => {
        if (this.hasTemplateSummary(templateId)) {
          return this.templateSummary(templateId).status;
        }
        if (this.hasTemplate(templateId)) {
          return this.template(templateId).status;
        }

        return undefined;
      };
    },

    activitySummary() {
      return (
        templateId: string,
        activityId: string,
      ): ActivityTemplateSummary => {
        const activitySummary = this.template(templateId).activities.find(
          (act) => act.id === activityId,
        );
        if (!activitySummary) {
          throw new Error('Missing activitySummary in templateStore');
        }

        return activitySummary;
      };
    },

    link() {
      return (templateId: string, linkId: string) => {
        const template = this.template(templateId);
        const link = template.links.find(({ id }) => id === linkId);
        if (!link) {
          throw new Error('Missing link in template');
        }

        return link;
      };
    },

    document() {
      return (templateId: string, docId: string) =>
        this.template(templateId).documents.find(({ id }) => id === docId);
    },

    allTags() {
      return (templateId: string): TagTemplate[] =>
        this.template(templateId)?.tags.sort((a, b) =>
          a.title.localeCompare(b.title),
        ) || [];
    },

    allLinks() {
      return (templateId: string): LinkTemplate[] =>
        this.template(templateId)?.links.sort((a, b) =>
          a.title.localeCompare(b.title),
        ) || [];
    },

    allDocuments() {
      return (templateId: string): DocumentTemplate[] =>
        this.template(templateId)?.documents.sort((a, b) =>
          a.title.localeCompare(b.title),
        ) || [];
    },

    tag() {
      return (templateId: string, tagId: string): TagTemplate => {
        const tag = this.allTags(templateId).find((tag) => tag.id === tagId);
        if (!tag) {
          throw new Error('Missing tag in templateStore');
        }

        return tag;
      };
    },

    allTopicTags() {
      return (templateId: string): TagTemplate[] => {
        return this.allTags(templateId).filter((tag) => tag.topic);
      };
    },

    allNonTopicTags() {
      return (templateId: string): TagTemplate[] => {
        return this.allTags(templateId).filter((tag) => !tag.topic);
      };
    },

    workstream() {
      return (templateId: string, workstreamId: string): WorkstreamTemplate => {
        const workstream = this.template(templateId)?.workstreams.find(
          ({ id }) => id === workstreamId,
        );
        if (!workstream) {
          throw new Error(`Workstream ${workstreamId} not found`);
        }

        return workstream;
      };
    },

    workstreamActivities() {
      return (
        templateId: string,
        workstreamId: string,
      ): ActivityTemplateSummary[] => {
        const activities = this.template(templateId).activities;

        return activities.filter(
          ({ workstream }) => workstream === workstreamId,
        );
      };
    },

    phase() {
      return (templateId: string, phaseId: string) => {
        const phases = this.template(templateId).phases;
        const phase = phases.find(({ id }) => id === phaseId);
        if (!phase) {
          throw new Error(`Phase ${phaseId} not found`);
        }

        return phase;
      };
    },

    phases() {
      return (templateId: string): PhaseTemplate[] => {
        const template = this.template(templateId);
        if (!template) {
          throw new Error('Missing template gettting milestones:' + templateId);
        }

        return template.phases;
      };
    },

    workstreamPosition() {
      return (templateId: string, workstreamId: string): number => {
        const workstreams = this.template(templateId).workstreams;
        const position = workstreams.findIndex(({ id }) => id === workstreamId);
        if (position < 0) {
          throw new Error(`Workstream ${workstreamId} not found`);
        }

        return position;
      };
    },

    phasePosition() {
      return (templateId: string, phaseId: string): number => {
        const phases = this.template(templateId).phases;
        const position = phases.findIndex(({ id }) => id === phaseId);
        if (position < 0) {
          throw new Error(`Phase ${phaseId} not found`);
        }

        return position;
      };
    },

    // sorted template activities by sequence and workstreamPosition
    sortedActivities() {
      return (templateId: string): ActivityTemplateSummary[] => {
        const activities = this.template(templateId).activities.slice();

        return activities.sort((a, b) => {
          const aPhasePos = this.phasePosition(templateId, a.phase);
          const bPhasePos = this.phasePosition(templateId, b.phase);
          if (aPhasePos === bPhasePos) {
            if (a.position === b.position) {
              const aWsPos = this.workstreamPosition(templateId, a.workstream);
              const bWsPos = this.workstreamPosition(templateId, b.workstream);
              if (aWsPos === bWsPos) {
                return 0;
              }

              return aWsPos < bWsPos ? 1 : -1;
            }

            return a.position < b.position ? 1 : -1;
          }

          return aPhasePos < bPhasePos ? 1 : -1;
        });
      };
    },

    previousActivity() {
      return (
        templateId: string,
        activityId: string,
      ): ActivityTemplateSummary | undefined => {
        const sortedActivities = this.sortedActivities(templateId);
        const position = sortedActivities.findIndex(
          ({ id }) => activityId === id,
        );

        return sortedActivities[position - 1];
      };
    },

    nextActivity() {
      return (
        templateId: string,
        activityId: string,
      ): ActivityTemplateSummary | undefined => {
        const sortedActivities = this.sortedActivities(templateId);
        const position = sortedActivities.findIndex(
          ({ id }) => activityId === id,
        );

        return sortedActivities[position + 1];
      };
    },

    projectFunctions() {
      return (templateId: string) => this.template(templateId).functions;
    },

    projectFunction() {
      return (templateId: string, functionId: string) => {
        const projectFunction = this.template(templateId).functions.find(
          ({ id }) => id === functionId,
        );
        if (!projectFunction) {
          throw new Error(
            `Missing projectFunction (${functionId}) in template (${templateId}) `,
          );
        }

        return projectFunction;
      };
    },

    editorsTemplate() {
      return (templateId: string) => this.editorTemplates[templateId];
    },

    hasEditors(state) {
      return (templateId: string) => {
        return !!state.editorTemplates[templateId];
      };
    },

    milestoneSummary() {
      return (
        templateId: string,
        milestoneId: string,
      ): MilestoneTemplateSummary => {
        const phases = this.template(templateId).phases.slice();
        let summary: MilestoneTemplateSummary | undefined;
        while (!summary && phases.length) {
          const phase = phases.pop() as PhaseTemplate;
          summary =
            (phase.final_milestone.id === milestoneId
              ? phase.final_milestone
              : null) ||
            phase.intermediate_milestones.find(({ id }) => id === milestoneId);
        }
        if (!summary) {
          throw new Error('MilestoneSummary not found: ' + milestoneId);
        }

        return summary;
      };
    },

    milestones() {
      return (templateId: string): Milestone[] => {
        const template = this.template(templateId);
        if (!template) {
          throw new Error('Missing template gettting milestones:' + templateId);
        }

        const milestones = [] as Milestone[];

        template.phases.forEach((phase) => {
          const phaseMilestones: Milestone[] =
            phase.intermediate_milestones.map((d) => ({
              ...d,
              metadata: {
                phaseId: phase.id,
                isFinal: false,
              },
            }));

          milestones.push(...phaseMilestones);
          milestones.push({
            ...phase.final_milestone,
            metadata: {
              phaseId: phase.id,
              isFinal: true,
            },
          });
        });

        return milestones;
      };
    },

    milestone() {
      return (templateId: string, milestoneId: string) => {
        const milestone = this.milestones(templateId).find(
          ({ id }) => id === milestoneId,
        );
        if (!milestone) {
          throw new Error(
            `Milestone ${milestoneId} not found in template ${templateId}`,
          );
        }

        return milestone;
      };
    },

    milestonePhase() {
      return (templateId: string, milestoneId: string) => {
        const phases = this.template(templateId).phases;
        const phase = phases.find((phase) => {
          return (
            phase.final_milestone.id === milestoneId ||
            phase.intermediate_milestones.find(({ id }) => id === milestoneId)
          );
        });
        if (!phase) {
          throw new Error('Phase of milestone' + milestoneId + 'not found');
        }

        return phase;
      };
    },

    activitiesInPhase() {
      return (templateId: string, phaseId: string) => {
        const template = this.template(templateId);

        return template.activities.filter(({ phase }) => phase === phaseId);
      };
    },

    successorsList() {
      return (
        templateId: string,
        activityId: string,
      ): ActivityTemplateSummary[] => {
        const activities = this.template(templateId).activities;

        return activities.filter(({ predecessors }) =>
          predecessors.includes(activityId),
        );
      };
    },

    criterion() {
      return (templateId: string, activityId: string, criterionId: string) => {
        const activity = this.activitySummary(templateId, activityId);
        const criterion = activity.completion_criteria.find(
          ({ id }) => criterionId === id,
        );
        if (!criterion) {
          throw new Error(
            `Missing completion criterion (${criterionId}) in activity (${activityId}) template`,
          );
        }

        return criterion;
      };
    },
  },

  actions: {
    write(template: ProjectMapTemplate) {
      const persistedTemplate = this.templates.find(
        (temp) => temp.id === template.id,
      );
      if (persistedTemplate) {
        Object.assign(persistedTemplate, template);
      } else {
        this.templates.push(template);
      }

      return this.template(template.id);
    },

    writeActivitySummary(
      templateId: string,
      activity: ActivityTemplateSummary,
    ) {
      const template = this.template(templateId);
      const persistedActivity = template.activities.find(
        ({ id }) => id === activity.id,
      );
      if (persistedActivity) {
        Object.assign(persistedActivity, activity);
      } else {
        const template = this.template(templateId);
        template.activities.push(activity);
      }

      return this.activitySummary(templateId, activity.id);
    },

    writeActivityTags(
      templateId: string,
      activityId: string,
      options: { add?: string[]; remove?: string[] },
    ) {
      const template = this.template(templateId);
      const activity = template.activities.find(({ id }) => id === activityId);
      if (!activity) {
        return;
      }

      const { add, remove } = options;

      if (add) {
        add.forEach((tagId) => {
          if (!activity.tags.includes(tagId)) {
            activity.tags.push(tagId);
          }
        });
      }

      if (remove) {
        activity.tags = activity.tags.filter(
          (tagId) => !remove.includes(tagId),
        );
      }
    },

    async removeActivty(templateId: string, activityId: string) {
      const errors = useActivityTemplateRulesStore().checkRemoveActivity(
        templateId,
        activityId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.deleteActvity(templateId, activityId);
    },

    async removeActivityPredecessor(
      templateId: string,
      activityId: string,
      predecessorId: string,
    ) {
      await TemplatesService.activityTemplateDeletePredecessor(
        templateId,
        activityId,
        predecessorId,
      );
    },

    writeWorkstream(
      templateId: string,
      workstream: WorkstreamTemplate,
      before?: string,
    ) {
      const template = this.template(templateId);
      const existing = template.workstreams.find(
        ({ id }) => id === workstream.id,
      );
      if (!existing) {
        const beforeIndex = template.workstreams.findIndex(
          ({ id }) => id === before,
        );
        if (beforeIndex === -1) {
          template.workstreams.push(workstream);
        } else {
          template.workstreams.splice(beforeIndex, 0, workstream);
        }
      } else {
        Object.assign(existing, workstream);
      }

      return this.workstream(templateId, workstream.id);
    },

    writeLink(templateId: string, link: LinkTemplate) {
      const template = this.template(templateId) as ProjectMapTemplate;
      const persistedLink = template.links.find(({ id }) => id === link.id);
      if (persistedLink) {
        Object.assign(persistedLink, link);
      } else {
        template.links.push(link);
      }

      return this.link(templateId, link.id);
    },

    writeTag(templateId: string, tag: TagTemplate) {
      const template = this.template(templateId);
      if (!template) {
        throw new Error('Missing template in templateStore');
      }
      const persistedTag = template.tags.find(({ id }) => id === tag.id);
      if (persistedTag) {
        Object.assign(persistedTag, tag);
      } else {
        template.tags.push(tag);
      }

      return this.document(templateId, tag.id);
    },

    writeDocument(templateId: string, doc: DocumentTemplate) {
      const template = this.template(templateId) as ProjectMapTemplate;
      const persistedDoc = template.documents.find(({ id }) => id === doc.id);
      if (persistedDoc) {
        Object.assign(persistedDoc, doc);
      } else {
        template.documents.push(doc);
      }

      return this.document(templateId, doc.id);
    },

    writeCompletionCriterion(
      templateId: string,
      activityId: string,
      completionCriterion: CompletionCriterionTemplate,
    ) {
      const activity = this.activitySummary(templateId, activityId);
      const criterion = activity.completion_criteria?.find(
        ({ id }) => id === completionCriterion.id,
      );

      if (!criterion) {
        activity.completion_criteria.push(completionCriterion);
      } else {
        Object.assign(criterion, completionCriterion);
      }
      useActivityTemplateStore().writeCachedCriterion(
        templateId,
        activityId,
        completionCriterion,
      );

      return this.criterion(templateId, activityId, completionCriterion.id);
    },

    /**
     * Removes a criterion the server no longer has on the activity (deleted,
     * moved) from both local copies: the summary, which the map lists, and
     * the cached details, which the reorder check reads. With only one of
     * them updated, reordering failed until a reload (WI #5801).
     */
    removeCriterionLocally(
      templateId: string,
      activityId: string,
      criterionId: string,
    ) {
      const activityTemplateStore = useActivityTemplateStore();
      const lists: CompletionCriterionTemplate[][] = [];
      if (
        this.hasTemplate(templateId) &&
        this.template(templateId).activities.some(({ id }) => id === activityId)
      ) {
        lists.push(
          this.activitySummary(templateId, activityId).completion_criteria,
        );
      }
      if (activityTemplateStore.hasActivity(templateId, activityId)) {
        lists.push(
          activityTemplateStore.activity(templateId, activityId)
            .completion_criteria,
        );
      }
      // The two copies can share one array, so look the criterion up in each
      for (const criteria of lists) {
        const index = criteria.findIndex(({ id }) => id === criterionId);
        if (index > -1) {
          criteria.splice(index, 1);
        }
      }
    },

    async loadTemplate(templateId: string) {
      const { template } = await TemplatesService.getTemplate(templateId);
      this.write(template);

      return this.template(template.id);
    },

    async loadList() {
      const { templates } = await TemplatesService.getTemplates();
      this.templateLinks.length = 0;
      this.templateLinks.push(...templates);

      return this.templateLinks;
    },

    async loadTemplateDetailsList() {
      this.templatesDetails = await TemplatesService.getallTemplates();

      return this.templatesDetails;
    },

    async copy(payload: CreateProjectMapTemplateRequest) {
      const template = await this.create(payload);
      this.write(template);
      await this.loadList();
    },

    async create(
      payload: CreateProjectMapTemplateRequest,
    ): Promise<ProjectMapTemplate> {
      const errors = useTemplateRulesStore().checkCreateTemplate(payload);
      if (errors) {
        throw errors;
      }
      const { template } = await TemplatesService.createTemplate(payload);
      this.templates.push(template);
      await this.loadList();

      return this.template(template.id) as ProjectMapTemplate;
    },

    async updateDetails(
      templateId: string,
      payload: UpdateProjectMapTemplateDetailsRequest,
    ) {
      const errors = useTemplateRulesStore().checkUpdateTemplateDetails(
        templateId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      const previousOwner = this.templateBasics(templateId)?.owner;
      await TemplatesService.updateTemplateDetails(templateId, payload).then(
        ({ template }) => {
          const isOwnerChange =
            !!previousOwner && previousOwner.id !== template.owner?.id;
          const editors = this.editorTemplates[templateId];
          if (editors && template.owner) {
            // As on the server: the new owner leaves the editors, and so does the
            // previous one unless kept.
            this.editorTemplates[templateId] = editors.filter(
              (editor) =>
                editor.id !== template.owner.id &&
                !(isOwnerChange && editor.id === previousOwner.id),
            );
            if (isOwnerChange && payload.keep_previous_owner_as_editor) {
              this.editorTemplates[templateId].push(previousOwner);
            }
          }

          const persistedTemplate = this.templates.find(
            (temp) => temp.id === template.id,
          );
          if (persistedTemplate) {
            Object.assign(persistedTemplate, template);
          }

          const templateLink = this.templateLinks.find(
            (temp) => temp.id === template.id,
          );
          if (templateLink) {
            templateLink.title = template.title;
            templateLink.scopes = template.scopes;
            templateLink.status = template.status;
            templateLink.custom_properties = template.custom_properties;
            templateLink.owner = template.owner;
          }

          const details = this.templatesDetails.find(
            (temp) => temp.id === template.id,
          );
          if (details) {
            details.title = template.title;
            details.scopes = template.scopes;
            details.status = template.status;
            details.custom_properties = template.custom_properties;
            details.owner = template.owner;
          }
        },
      );
    },

    async update(templateId: string, payload: UpdateProjectMapTemplateRequest) {
      const errors = useTemplateRulesStore().checkUpdateTemplate(
        templateId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.updateTemplate(templateId, payload).then(
        ({ template }) => {
          const persistedTemplate = this.templates.find(
            (temp) => temp.id === template.id,
          );
          if (persistedTemplate) {
            Object.assign(persistedTemplate, template);
          }
        },
      );
    },

    async updateStatus(templateId: string, status: TemplateStatus) {
      const errors = useTemplateRulesStore().checkUpdateTemplateStatus(
        templateId,
        status,
      );
      if (errors) {
        throw errors;
      }
      const { template } = await TemplatesService.updateTemplateStatus(
        templateId,
        { status },
      );

      const templateLink = this.templateLinks.find(
        (temp) => temp.id === template.id,
      );
      if (templateLink) {
        Object.assign(templateLink, template);
      }

      // The loaded map carries the status too, for the badge in the map header.
      const persistedTemplate = this.templates.find(
        (temp) => temp.id === template.id,
      );
      if (persistedTemplate) {
        persistedTemplate.status = template.status;
      }

      const details = this.templatesDetails.find(
        (temp) => temp.id === template.id,
      );
      if (details) {
        details.status = template.status;
      }

      return template;
    },

    async delete(templateId: string) {
      const errors = useTemplateRulesStore().checkRemoveTemplate(templateId);
      if (errors) {
        throw errors;
      }
      await TemplatesService.removeTemplate(templateId);
      this.templateLinks = this.templateLinks.filter(
        (link) => link.id !== templateId,
      );
      this.templates = this.templates.filter(
        (template) => template.id !== templateId,
      );
      this.templatesDetails = this.templatesDetails.filter(
        (details) => details.id !== templateId,
      );
    },

    // TAGS
    async fetchTags(templateId: string) {
      const { tags } = await TemplatesService.getTags(templateId);
      const template = this.template(templateId) as ProjectMapTemplate;
      template.tags = tags;

      return this.allTags(templateId);
    },

    // WORKSTREAMS
    async fetchWorkstream(templateId: string, workstreamId: string) {
      const { workstream } = await TemplatesService.getWorkstream(
        templateId,
        workstreamId,
      );

      return this.writeWorkstream(templateId, workstream);
    },

    async addWorkstream(
      templateId: string,
      payload: WorkstreamTemplateCreate,
      before?: string,
    ) {
      const errors = useTemplateRulesStore().checkTemplateAppendWorkstream(
        templateId,
        payload,
        before,
      );
      if (errors) {
        throw errors;
      }
      const { workstream } = await TemplatesService.addWorkstream(
        templateId,
        payload,
        before,
      );

      return this.writeWorkstream(templateId, workstream, before);
    },

    async updateWorkstream(
      templateId: string,
      workstreamId: string,
      payload: WorkstreamTemplateUpdate,
    ) {
      const errors = useTemplateRulesStore().checkTemplateUpdateWorkstream(
        templateId,
        payload,
        workstreamId,
      );
      if (errors) {
        throw errors;
      }
      const { workstream } = await TemplatesService.updateWorkstream(
        templateId,
        workstreamId,
        payload,
      );

      return this.writeWorkstream(templateId, workstream);
    },

    async deleteWorkstream(templateId: string, workstreamId: string) {
      const errors = useTemplateRulesStore().checkTemplateRemoveWorkstream(
        templateId,
        workstreamId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.deleteWorkstream(templateId, workstreamId);
      const template = this.template(templateId) as ProjectMapTemplate;
      const streamIndex = template.workstreams.findIndex(
        ({ id }) => id === workstreamId,
      );
      if (streamIndex > -1) {
        template.workstreams.splice(streamIndex, 1);
      }

      return true;
    },

    // PHASES
    writePhase(templateId: string, phase: PhaseTemplate, before?: string) {
      const template = this.template(templateId);

      const existing = template.phases.find(({ id }) => id === phase.id);
      if (!existing) {
        const beforeIndex = template.phases.findIndex(
          ({ id }) => id === before,
        );
        if (beforeIndex === -1) {
          template.phases.push(phase);
        } else {
          template.phases.splice(beforeIndex, 0, phase);
        }
      } else {
        Object.assign(existing, phase);
      }

      return existing || phase;
    },

    async updatePhase(
      templateId: string,
      phaseId: string,
      payload: PhaseTemplateUpdate,
    ) {
      await TemplatesService.updatePhase(templateId, phaseId, payload);
    },

    // FUNCTIONS
    async fetchFunctions(templateId: string) {
      const { functions } =
        await TemplatesService.getProjectFunctions(templateId);
      const template = this.template(templateId);
      template.functions = functions;
    },

    async createFunction(
      templateId: string,
      payload: CreateProjectFunctionRequest,
    ) {
      const errors = useTemplateRulesStore().checkTemplateAddFunction(
        templateId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.createProjectFunction(templateId, payload).then(
        ({ project_function }) => {
          const temp = this.template(templateId) as ProjectMapTemplate;
          temp.functions.push(project_function);
        },
      );
    },

    async updateFunction(
      templateId: string,
      functionId: string,
      payload: UpdateProjectFunctionRequest,
    ) {
      const errors = useTemplateRulesStore().checkTemplateUpdateFunction(
        templateId,
        payload,
        functionId,
      );
      if (errors) {
        throw errors;
      }
      const { project_function: newProjectFunction } =
        await TemplatesService.updateProjectFunction(
          templateId,
          functionId,
          payload,
        );
      const template = this.template(templateId) as ProjectMapTemplate;
      const persistedProjectFunction = template.functions.find(
        (fn) => fn.id === functionId,
      );

      if (persistedProjectFunction === undefined) {
        throw Error('Can not find template to patch');
      }

      Object.assign(persistedProjectFunction, newProjectFunction);

      return persistedProjectFunction;
    },

    async removeFunction(templateId: string, functionId: string) {
      const errors = useTemplateRulesStore().checkTemplateRemoveFunction(
        templateId,
        functionId,
      );
      if (errors) {
        throw errors;
      }

      return await TemplatesService.deleteProjectMapTemplateFunction(
        templateId,
        functionId.toString(),
      ).then(() => {
        const template = this.template(templateId) as ProjectMapTemplate;
        const fnIndex = template.functions.findIndex(
          (fn) => fn.id === functionId,
        );
        if (fnIndex > -1) {
          template.functions.splice(fnIndex, 1);
        }
      });
    },

    // LINKS
    async fetchLinks(templateId: string) {
      const { links } = await TemplatesService.getLinks(templateId);
      const template = this.template(templateId) as ProjectMapTemplate;
      template.links = links;

      return links;
    },

    async createLink(templateId: string, payload: CreateLinkTemplateRequest) {
      const errors = useTemplateRulesStore().checkTemplateAddLink(
        templateId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      const { link } = await TemplatesService.createLink(templateId, payload);

      if (payload.activity_id) {
        await this.fetchLinks(templateId);
        const activity = useActivityTemplateStore().activity(
          templateId,
          payload.activity_id,
        );
        activity.links.push(link.id);

        return;
      }

      const template = this.template(templateId) as ProjectMapTemplate;
      template.links.push(link);
    },

    async updateLink(
      templateId: string,
      linkId: string,
      payload: UpdateLinkTemplateRequest,
    ) {
      const errors = useTemplateRulesStore().checkTemplateUpdateLink(
        templateId,
        payload,
        linkId,
      );
      if (errors) {
        throw errors;
      }
      const { link } = await TemplatesService.updateLink(
        templateId,
        linkId,
        payload,
      );

      return this.writeLink(templateId, link);
    },

    deleteLink(templateId: string, linkId: string) {
      const errors = useTemplateRulesStore().checkTemplateRemoveLinkTemplate(
        templateId,
        linkId,
      );

      if (errors) {
        throw errors;
      }

      const spliceTemplateLinks = () => {
        const template = this.template(templateId) as ProjectMapTemplate;
        const linkIndex = template.links.findIndex(({ id }) => id === linkId);

        if (linkIndex > -1) {
          template.links.splice(linkIndex, 1);
        }
      };

      return TemplatesService.deleteLink(templateId, linkId).then(
        spliceTemplateLinks,
      );
    },

    deleteUnreferencedLinks(templateId: string): Promise<number> {
      return TemplatesService.deleteUnreferencedLinks(templateId).finally(() =>
        this.loadTemplate(templateId),
      );
    },

    /**
     * Picks company links into the template (WI #5775), one request per link
     * and one after the other. Returns the links that could not be added; the
     * template's links are reloaded either way.
     */
    async pickCompanyLinks(
      templateId: string,
      companyLinks: CompanyLink[],
    ): Promise<CompanyLink[]> {
      const failed: CompanyLink[] = [];

      for (const companyLink of companyLinks) {
        const payload = toCompanyLinkPickRequest(companyLink);

        try {
          const errors = useTemplateRulesStore().checkTemplateAddLink(
            templateId,
            payload,
          );
          if (errors) {
            throw errors;
          }
          await TemplatesService.createLink(templateId, payload);
        } catch (err) {
          // The caller names the failed links; the reason is logged, so that a
          // 401 or a 5xx can be told from a company link deleted meanwhile.
          console.error(`Could not pick company link ${companyLink.id}:`, err);
          failed.push(companyLink);
        }
      }
      await this.fetchLinks(templateId);

      return failed;
    },

    // DOCUMENTS
    async fetchDocuments(templateId: string) {
      const { documents } =
        await TemplatesService.getProjectDocuments(templateId);
      const template = this.template(templateId);
      if (template) {
        template.documents = documents;
      }
    },

    async createDocument(
      templateId: string,
      payload: CreateDocumentTemplateRequest,
    ) {
      const errors = useTemplateRulesStore().checkTemplateAddDocument(
        templateId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.createDocument(templateId, payload);
      // fetch all the documents again because the reference is not returned in the data above
      await this.fetchDocuments(templateId);

      if (payload.activity_id) {
        // update the activity documents since the document was created inside an activity
        await useActivityTemplateStore().loadDocuments(
          templateId,
          payload.activity_id,
        );
      }
    },

    async updateDocument(
      templateId: string,
      documentId: string,
      payload: UpdateDocumentTemplateRequest,
    ) {
      const errors = useTemplateRulesStore().checkTemplateUpdateDocument(
        templateId,
        documentId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      const { document: doc } = await TemplatesService.updateDocument(
        templateId,
        documentId,
        payload,
      );

      return this.writeDocument(templateId, doc);
    },

    async deleteDocument(templateId: string, docId: string) {
      const errors = useTemplateRulesStore().checkTemplateRemoveDocument(
        templateId,
        docId,
      );

      if (errors) {
        throw errors;
      }

      await TemplatesService.removeDocument(templateId, docId);

      const template = this.template(templateId) as ProjectMapTemplate;
      const docIndex = template.documents.findIndex(({ id }) => id === docId);

      if (docIndex > -1) {
        template.documents.splice(docIndex, 1);
      }

      // Strip it from the summaries too, so the shared_document check doesn't
      // report a document that no longer exists until the next reload.
      for (const activity of template.activities) {
        if (activity.documentsIds?.includes(docId)) {
          activity.documentsIds = activity.documentsIds.filter(
            (id) => id !== docId,
          );
          activity.documents = activity.documents?.filter(
            (doc) => doc.id !== docId,
          );
        }
      }
    },

    deleteUnreferencedDocuments(templateId: string): Promise<number> {
      return TemplatesService.deleteUnreferencedDocuments(templateId).finally(
        () => this.loadTemplate(templateId),
      );
    },

    /**
     * Picks company documents into the template (WI #5775), one request per
     * document and one after the other. Returns the documents that could not
     * be added; the template's documents are reloaded either way.
     */
    async pickCompanyDocuments(
      templateId: string,
      companyDocuments: CompanyDocument[],
    ): Promise<CompanyDocument[]> {
      const failed: CompanyDocument[] = [];

      for (const companyDocument of companyDocuments) {
        const payload = toCompanyDocumentPickRequest(companyDocument);

        try {
          const errors = useTemplateRulesStore().checkTemplateAddDocument(
            templateId,
            payload,
          );
          if (errors) {
            throw errors;
          }
          await TemplatesService.createDocument(templateId, payload);
        } catch (err) {
          // The caller names the failed documents; the reason is logged, so
          // that a 401 or a 5xx can be told from a company document deleted
          // meanwhile.
          console.error(
            `Could not pick company document ${companyDocument.id}:`,
            err,
          );
          failed.push(companyDocument);
        }
      }
      await this.fetchDocuments(templateId);

      return failed;
    },

    // EDITORS
    async fetchEditors(templateId: string) {
      const { users } = await TemplatesService.getTemplateEditor(templateId);
      const editorsTemplate = (this.editorTemplates[templateId] =
        this.editorTemplates[templateId] || []);
      editorsTemplate.splice(0, editorsTemplate.length, ...users);
    },

    async addTemplateEditors(templateId: string, userIds: string[]) {
      const errors = await useTemplateRulesStore().checkAddTemplateEditor(
        templateId,
        userIds,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.addTemplateEditor(templateId, {
        user_ids: userIds,
      });
      userIds.forEach((userId) => {
        const user = useUserStore().user(userId);
        this.editorsTemplate(templateId).push(user);
      });
    },

    async removeTemplateEditor(templateId: string, userId: string) {
      const errors = useTemplateRulesStore().checkRemoveEditor(
        templateId,
        userId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.removeTemplateEditor(templateId, userId);
      const editorsTemplate = this.editorTemplates[templateId] as UserSummary[];
      const editorIndex = editorsTemplate.findIndex(
        (editor) => editor.id === userId,
      );
      if (editorIndex > -1) {
        editorsTemplate.splice(editorIndex, 1);
      }
    },

    // CRITERIA
    async createCompletionCriterion(
      templateId: string,
      activityId: string,
      payload: CompletionCriterionTemplateCreate,
      index?: number,
    ) {
      const errors = useTemplateRulesStore().checkAddCompletionCriterion(
        templateId,
        activityId,
        index,
      );

      if (errors) {
        throw errors;
      }

      const { completion_criterion } =
        await TemplatesService.completionCriterionTemplateCreate(
          templateId,
          activityId,
          payload,
          index,
        );

      return this.writeCompletionCriterion(
        templateId,
        activityId,
        completion_criterion,
      );
    },

    async updateCompletionCriterion(
      templateId: string,
      activityId: string,
      completionCriterionId: string,
      payload: CompletionCriterionTemplateUpdate,
    ) {
      const errors = useTemplateRulesStore().checkUpdateCompletionCriterion(
        templateId,
        activityId,
        completionCriterionId,
        payload,
      );

      if (errors) {
        throw errors;
      }
      const criterion = await TemplatesService.updateCompletionCriterion(
        templateId,
        activityId,
        completionCriterionId,
        payload,
      );
      this.writeCompletionCriterion(
        templateId,
        activityId,
        criterion.completion_criterion,
      );
    },

    async updateCompletionCriteriaOrder(
      templateId: string,
      activityId: string,
      order: string[],
    ) {
      const payload = {
        completion_criteria_order: order,
      };
      const errors = useActivityTemplateRulesStore().checkUpdateActivity(
        templateId,
        activityId,
        payload,
      );
      if (errors) {
        throw errors;
      }

      return TemplatesService.updateActivity(
        templateId,
        activityId,
        payload,
      ).then(({ activity }) => {
        // The reorder check reads the cached details (WI #5801)
        const activityTemplateStore = useActivityTemplateStore();
        if (activityTemplateStore.hasActivity(templateId, activityId)) {
          activityTemplateStore.write(templateId, structuredClone(activity));
        }
        this.writeActivitySummary(templateId, activity);
      });
    },

    async removeCompletionCriterion(
      templateId: string,
      activityId: string,
      criterionId: string,
    ) {
      const errors = useTemplateRulesStore().checkRemoveCompletionCriterion(
        templateId,
        activityId,
        criterionId,
      );

      if (errors) {
        throw errors;
      }

      await TemplatesService.activityTemplateDeleteCompletionCriterionTemplate(
        templateId,
        activityId,
        criterionId,
      );
      this.removeCriterionLocally(templateId, activityId, criterionId);
    },

    async moveCompletionCriterion(
      templateId: string,
      sourceActivityId: string,
      completionCriterionId: string,
      payload: MoveCompletionCriterionTemplateRequest,
    ) {
      const completionCriterion = this.criterion(
        templateId,
        sourceActivityId,
        completionCriterionId,
      );
      await TemplatesService.moveCompletionCriterionTemplate(
        templateId,
        sourceActivityId,
        completionCriterionId,
        payload,
      );
      this.removeCriterionLocally(
        templateId,
        sourceActivityId,
        completionCriterionId,
      );
      // Also writes the target's cached details (WI #5801)
      this.writeCompletionCriterion(
        templateId,
        payload.target_activity_id,
        completionCriterion,
      );
    },
    async mergeFunction(
      templateId: string,
      functionId: string,
      requestBody: MergeFunctionRequest,
    ) {
      return TemplatesService.mergeFunction(
        templateId,
        functionId,
        requestBody,
      ).then(async (func) => {
        const funcToBeUpdated = this.projectFunction(templateId, functionId);
        Object.assign(funcToBeUpdated, func);
        const template = this.template(templateId);
        const functionIndex = template.functions.findIndex(
          (func) => func.id === requestBody.function_id,
        );
        if (functionIndex > -1) {
          template.functions.splice(functionIndex, 1);
        }
      });
    },

    async performSemanticSearch(templateId: string, query: string) {
      return TemplatesService.searchTemplate(templateId, query);
    },
  },
});
